import React, { useEffect, useRef, useState } from 'react';
import http from 'src/services/httpService';
import Button from 'src/componentes/button';
import Dialog from 'src/componentes/dialog';
import { FormattedMessage, useIntl } from 'react-intl';
import LoadingContainer from 'src/componentes/loading-container';
import Checkbox from 'src/componentes/checkbox';
import errorHandler from 'src/utils/error-handler';
import { useSnackbar } from 'notistack';
import { Row, Col } from 'src/componentes/grid';
import useAuth from 'src/hooks/useAuth';
import LucideIcon from 'src/componentes/lucide-icon';
import ValidacaoRestricaoLista from 'src/componentes/validacao-restricao-lista/ValidacaoRestricaoLista';
import { capitalizeFirstLetter } from 'src/utils/string';

export default function ValidarRestricao({
  url,
  urlExclusao,
  item,
  title = '',
  titleValues = {},
  description = '',
  subDescription = '',
  atualizar = () => {},
  onClose = () => {},
  afterRemove = () => {},
  urlList = '',
  handleClose = () => {},
  exibirEstouCiente = true,
  labelCancelar = 'labelManterVinculo',
  labelAlterar = 'labelExcluirVinculo',
  width = 700,
  minHeight = 500,
  customHandleSalvarClick = null,
  customHandleCloseValidarRestricaoClick = null,
  restricoesModel = null,
  ...rest
}) {
  const { terms: resources } = useAuth();
  const [isLoading, setIsLoading] = useState(!!url && !restricoesModel);
  const [model, setModel] = useState(restricoesModel);
  const [estouCiente, setEstouCiente] = useState(false);
  const intl = useIntl();
  const modalRef = useRef();
  const exclusaoEmAndamentoRef = useRef(false);
  const { enqueueSnackbar } = useSnackbar();
  const exclusaoBloqueada =
    (!!url && model == null) ||
    model?.podeExcluir === false ||
    !!model?.restricoes?.some((restricao) => restricao.podeExcluir === false);
  const confirmacaoDesabilitada =
    isLoading || exclusaoBloqueada || (exibirEstouCiente && !estouCiente);

  const handleCloseValidarRestricao = () => {
    if (customHandleCloseValidarRestricaoClick) {
      customHandleCloseValidarRestricaoClick();
    }

    if (modalRef.current) modalRef.current.close();

    handleClose && handleClose();
  };

  const handleSalvarClick = async (registro) => {
    if (confirmacaoDesabilitada) return;

    if (customHandleSalvarClick) {
      customHandleSalvarClick();

      if (modalRef.current) modalRef.current.close();

      handleClose && handleClose();

      return;
    }

    if (urlExclusao) {
      if (exclusaoEmAndamentoRef.current) return;

      exclusaoEmAndamentoRef.current = true;
      setIsLoading(true);
      await http
        .post(urlExclusao, {
          id: registro.id,
        })
        .then(() => {
          setIsLoading(false);
          enqueueSnackbar(
            intl.formatMessage({ id: 'label.registroExcluidoComSucesso' }),
            {
              variant: 'success',
            }
          );
          afterRemove && afterRemove(registro);
          onClose && onClose(true);
          handleCloseValidarRestricao(true);
        })
        .catch((error) => {
          errorHandler(error);
          setIsLoading(false);
        });
      exclusaoEmAndamentoRef.current = false;
    }
    setIsLoading(false);
    atualizar && atualizar(handleCloseValidarRestricao());
    if (urlList) {
      window.location.href = urlList;
    }
  };

  const actions = [
    <Button
      key={1}
      data-testid="validar-restricao-cancel-button"
      onClick={() => handleCloseValidarRestricao()}
      color="secondary"
    >
      <FormattedMessage id={labelCancelar} />
    </Button>,
    <Button
      key={2}
      data-testid="validar-restricao-confirm-button"
      disabled={confirmacaoDesabilitada}
      color="danger"
      className="ml-2"
      onClick={() => handleSalvarClick(item)}
    >
      <FormattedMessage id={labelAlterar} />
    </Button>,
  ];

  useEffect(() => {
    if (url && model == null) {
      setIsLoading(true);

      const parametros = { id: item.id };
      http
        .post(url, parametros)
        .then((response) => {
          setModel(response.data);
          setIsLoading(false);
        })
        .catch((error) => {
          errorHandler(error);
          setIsLoading(false);
        });
    }
  }, [url, item]);

  return (
    <Dialog
      ref={modalRef}
      actions={actions}
      width={width}
      minHeight={minHeight}
      padContent
      onRequestClose={handleCloseValidarRestricao}
      mouseDownClose={false}
      {...rest}
      textCapitalizeTitle={false}
      titleCapitalizeNone
    >
      <LoadingContainer isLoading={isLoading}>
        <div>
          <Row>
            <Col className="d-flex justify-content-center" md={2}>
              <div className="tw:flex tw:size-14 tw:items-center tw:justify-center tw:rounded-full tw:bg-[#FFEBEB]">
                <LucideIcon icon="AlertTriangle" size={30} color="red" />
              </div>
            </Col>
            <Col>
              <div className="tw:text-xl tw:text-[#A90F0F]">
                <span>
                  {capitalizeFirstLetter(
                    intl.formatMessage(
                      { id: title },
                      {
                        pontodecontrole: resources.pontodecontrole,
                        pontosdecontrole: resources.pontosdecontrole,
                        revisaodecontrole: resources.revisaodecontrole,
                        revisoesdecontrole: resources.revisoesdecontrole,
                        risco: resources.risco,
                        riscos: resources.riscos,
                        avaliacoes: resources.avaliacoes,
                        cicloavaliacao: resources.cicloavaliacao,
                        ...titleValues,
                      }
                    )
                  )}
                </span>
              </div>
              {(model?.description || description) && (
                <div className="pt-2">
                  <span className="tw:text-base">
                    {model?.description
                      ? model.description
                      : intl.formatMessage(
                          { id: description },
                          {
                            pontodecontrole: resources.pontodecontrole,
                            pontosdecontrole: resources.pontosdecontrole,
                            revisaodecontrole: resources.revisaodecontrole,
                            revisoesdecontrole: resources.revisoesdecontrole,
                            risco: resources.risco,
                            riscos: resources.riscos,
                            avaliacoes: resources.avaliacoes,
                            cicloavaliacao: resources.cicloavaliacao,
                          }
                        )}
                  </span>
                </div>
              )}
              {subDescription && (
                <div className="pt-2">
                  <span className="tw:text-base">
                    {intl.formatMessage({ id: subDescription })}
                  </span>
                </div>
              )}
              <div className="pt-3">
                <div className="tw:rounded-[5px] tw:border tw:border-gray-200 tw:shadow-[0_1px_2px_rgb(0_0_0/0.04),0_2px_8px_-2px_rgb(0_0_0/0.08)]">
                  <div className="tw:mt-4">
                    <ValidacaoRestricaoLista
                      restricoes={model?.restricoes || []}
                    />
                  </div>
                </div>
                {exibirEstouCiente && !exclusaoBloqueada && (
                  <div className="pt-3">
                    <Checkbox
                      data-testid="validar-restricao-awareness-checkbox"
                      className="tw:text-base tw:leading-5 tw:[&>label]:gap-3"
                      model={{
                        label: intl.formatMessage({
                          id: 'cienteAcaoIrreversivel',
                        }),
                        value: estouCiente,
                        requestChange: setEstouCiente,
                      }}
                      color="primary"
                    />
                  </div>
                )}
              </div>
            </Col>
          </Row>
        </div>
      </LoadingContainer>
    </Dialog>
  );
}
